/**
 * Les attributs que Symfony signe dans les jetons LiveKit, et ce que les moteurs
 * vidéo et audio en lisent. Signés, donc crus : un client ne peut pas les
 * écrire. Voir « Attributs signés » dans `docs/spec/video-engine.md`.
 */

/** Porté par un participant qui n'est qu'un appareil appairé. */
export const DEVICE_KIND_ATTRIBUTE = "virtuosa.kind";

/** Sa valeur pour un appareil qui filme. */
export const PAIRED_CAMERA_KIND = "camera";

/** Sur un appareil appairé : la référence du compte pour qui il filme. */
export const OWNER_ATTRIBUTE = "virtuosa.owner";

/** Sur un participant authentifié : la référence de son compte dans la salle. */
export const USER_ATTRIBUTE = "virtuosa.user";

type Attributes = Readonly<Record<string, string>> | undefined;

/**
 * Vrai pour un participant qui n'est qu'un appareil appairé. Il n'a ni lane ni
 * place dans les listes : il ne joue pas, il n'écoute pas, il filme. Seul
 * l'attribut signé le dit — une identité ou un nom se contrefont.
 */
export function isPairedDevice(attributes: Attributes): boolean {
  return attributes?.[DEVICE_KIND_ATTRIBUTE] === PAIRED_CAMERA_KIND;
}

/**
 * À qui appartient ce que publie ce participant.
 *
 * Un appareil appairé appartient au compte qui l'a appairé ; un participant, à
 * son propre compte. Pas à l'identité LiveKit : elle vit par onglet et c'est le
 * navigateur qui la propose, si bien qu'un nouvel onglet la changerait et qu'un
 * client pourrait la contrefaire. Seul un invité sans compte n'a qu'elle — il
 * n'appaire rien, et personne n'a besoin de le reconnaître d'un onglet à
 * l'autre.
 */
export function ownerOf(identity: string, attributes: Attributes): string {
  const signed = isPairedDevice(attributes) ? attributes?.[OWNER_ATTRIBUTE] : attributes?.[USER_ATTRIBUTE];

  return signed !== undefined && signed !== "" ? signed : identity;
}

/**
 * Le propriétaire d'un appareil est-il parmi ces participants visibles ?
 *
 * Reconnu à la référence de son compte, quel que soit son onglet. Deux replis,
 * où n'importe quel participant qui n'est pas un appareil fait l'affaire :
 * sans référence connue (`null`), et dans une salle ouverte avant les
 * références de compte, où personne n'en porte — un interprète entré avant un
 * déploiement ne doit pas voir partir son téléphone au bout de trente
 * secondes.
 */
export function isOwnerPresent(
  participants: readonly { identity: string; attributes: Attributes }[],
  owner: string | null,
): boolean {
  const people = participants.filter((participant) => !isPairedDevice(participant.attributes));
  const referenced = people.some((participant) => (participant.attributes?.[USER_ATTRIBUTE] ?? "") !== "");

  if (owner === null || !referenced) {
    return people.length > 0;
  }

  return people.some((participant) => ownerOf(participant.identity, participant.attributes) === owner);
}
